import type { FilesFormatSchemaWithId } from '@iconik-io/iconik-api/src/services/files';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { createEntityDenormalizer } from '@iconik-io/shared-lib/src/redux/entities/normalizers';
import { selectLatestActiveVersionId } from '@iconik-io/shared-lib/src/redux/entities/selectors/versions';

import ContextMenu from '../../components/context_menu/context_menu';
import contextMenuConnector from '../../components/context_menu/context_menu_connector';
import type {
  ContextMenuItemDefinition,
  GetDefinition,
} from '../../components/context_menu/types';
import { ARCHIVED, ARCHIVING } from '../../constants/archive';
import { formatNames } from '../../constants/files';
import {
  CONFIRM_TRANSCODE,
  CONFIRM_TRANSCRIBE_SUBTITLE_MODAL,
  FORMATS_PANEL_ARCHIVE_MODAL,
  FORMATS_PANEL_DELETE_FILESETS,
  FORMATS_PANEL_EDIT_SUBTITLES,
  FORMATS_PANEL_METADATA_MODAL,
  FORMATS_PANEL_NEW_FORMAT,
  FORMATS_PANEL_RESTORE_MODAL,
  FORMATS_PANEL_TRANSFER_MODAL,
} from '../../constants/modals';
import {
  ASSET_PAGE_ROUTE,
  ASSET_VERSION_PAGE_ROUTE,
} from '../../constants/routes';
import {
  CONTEXT_MENU_PANELS_FILES_FORMATS,
  connectContextMenuConsumer,
} from '../../contexts/context_menu';
import { selectTranscriptionPropertiesByAssetId } from '../../ducks/asset_transcriptions/selectors';
import { transcribeSubtitle } from '../../ducks/asset_transcriptions/thunks';
import { selectFileSetsByFormatId } from '../../ducks/file_sets/selectors';
import { downloadFormatFile, openOriginalFile } from '../../ducks/files/thunks';
import { errorMessage } from '../../ducks/messages/thunks';
import { openModal } from '../../ducks/modals_manager/slice';
import { selectAssetFiles } from '../../ducks/pages/asset/selectors';
import { selectArchiveStorages } from '../../ducks/storages/selectors';
import { selectIsLatestVersion } from '../../ducks/version/selectors';
import { ICONS } from '../../icons/icon';
import {
  getCurrentRouteParams,
  getCurrentRoutePattern,
} from '../../router/current_route_params';
import type { AppDispatch, GetState } from '../../store/types';
import { isFormatGenericMedia } from '../../utils/media_type';
import separateMenuGroups from '../../utils/separate_menu_groups';
import { selectResolvedPermissions, selectSubtitles } from './selectors';
import { canDownloadFormat } from './utils';

const assetDenormalizer = createEntityDenormalizer('assets');

export const getDefinition: GetDefinition<FilesFormatSchemaWithId> = (
  props,
  state,
) => {
  const contextItems = props.contextItems;
  const [contextItem] = contextItems;

  const itemsOpen: ContextMenuItemDefinition[] = [];
  const itemsMain: ContextMenuItemDefinition[] = [];
  const itemsDelete: ContextMenuItemDefinition[] = [];

  if (!contextItem) {
    return { items: itemsMain };
  }

  const archiveStorages = selectArchiveStorages(state);
  const { userRoles } = state.rolesReducer;
  const { asset_id: assetId } = contextItem;
  const formatAsset = assetId
    ? assetDenormalizer(state.entities, assetId)
    : undefined;
  if (!assetId || !formatAsset) {
    return {
      items: itemsMain,
    };
  }
  const routeVersionId = getCurrentRouteParams().versionId;
  const formatFiles = selectAssetFiles(
    state,
    formatAsset.id,
    routeVersionId,
  ).filter((file) => file.format_id === contextItem.id);
  const formatFileSets = selectFileSetsByFormatId(state, contextItem.id);
  const searchItem: SearchItemEntity = {
    ...formatAsset,
    object_type: 'assets',
    formats: [contextItem],
    files: formatFiles,
  };
  const isLatestVersion = [ASSET_PAGE_ROUTE, ASSET_VERSION_PAGE_ROUTE].includes(
    getCurrentRoutePattern(),
  )
    ? selectIsLatestVersion(state, formatAsset.id, routeVersionId)
    : true;
  const mediaFormat =
    contextItem?.metadata?.length && isFormatGenericMedia(contextItem);
  const permissions = selectResolvedPermissions(state, formatAsset.id);

  if (contextItem.status === 'DELETED') {
    return {
      items: itemsMain,
    };
  }

  if (formatFiles && formatFiles.length) {
    if (formatFiles.find((f) => !f.url) && contextItem.asset_id) {
      // ISG files action
      itemsOpen.push({
        label: 'Open',
        action: () => (dispatch: AppDispatch) => {
          dispatch(openOriginalFile(assetId, contextItem.id));
        },
        icon: ICONS.Export,
      });
    }

    itemsMain.push({
      label: 'See Metadata',
      action: () => (dispatch: AppDispatch) => {
        dispatch(openModal({ id: FORMATS_PANEL_METADATA_MODAL, contextItems }));
      },
      icon: ICONS.Metadata,
    });

    if (
      canDownloadFormat(
        contextItem.id,
        userRoles.webCanDownloadOriginal,
        formatFiles,
        formatFileSets,
      )
    ) {
      itemsMain.push({
        label: 'Download',
        action: () => (dispatch: AppDispatch) => {
          dispatch(
            downloadFormatFile(
              assetId,
              contextItem.id,
              undefined,
              contextItem.name,
            ),
          );
        },
        icon: ICONS.Download,
        disabled: formatAsset.is_blocked,
      });
    }
  }

  if (userRoles.canWriteTransfers && contextItem.is_online) {
    itemsMain.push({
      label: 'Transfer/Copy…',
      action: () => (dispatch: AppDispatch) => {
        dispatch(
          openModal({
            id: FORMATS_PANEL_TRANSFER_MODAL,
            contextItems: [searchItem],
            options: {
              useFileSet: state.routeParams.route === ASSET_VERSION_PAGE_ROUTE,
            },
          }),
        );
      },
      icon: ICONS.Transfer,
    });
  }

  // An archive in progress can't be re-archived. An already-archived format can
  // only be re-archived once it is online again (e.g. after a restore).
  const canArchiveFormat =
    contextItem.archive_status !== ARCHIVING &&
    (contextItem.archive_status !== ARCHIVED || Boolean(contextItem.is_online));

  if (
    userRoles.canArchiveFormats &&
    permissions.write &&
    archiveStorages.length > 0 &&
    canArchiveFormat
  ) {
    itemsMain.push({
      label: 'Archive Format…',
      action: () => (dispatch: AppDispatch) => {
        dispatch(
          openModal({
            id: FORMATS_PANEL_ARCHIVE_MODAL,
            contextItems: [searchItem],
            options: {
              bulkOperation: false,
            },
          }),
        );
      },
      icon: ICONS.Archive,
    });
  }

  if (
    userRoles.canRestoreArchivedFormats &&
    contextItem.archive_status === ARCHIVED
  ) {
    itemsMain.push({
      label: 'Restore Format…',
      action: () => (dispatch: AppDispatch) => {
        dispatch(
          openModal({
            id: FORMATS_PANEL_RESTORE_MODAL,
            contextItems: [searchItem],
            options: {
              bulkOperation: false,
            },
          }),
        );
      },
      icon: ICONS.Restore,
    });
  }

  if (
    mediaFormat &&
    userRoles.canWriteFiles &&
    userRoles.canWriteProxies &&
    isLatestVersion &&
    permissions.write &&
    contextItem.name !== formatNames.WILDMOKA
  ) {
    itemsMain.push({
      label: 'Re-transcode…',
      action: () => (dispatch: AppDispatch) => {
        dispatch(
          openModal({
            id: CONFIRM_TRANSCODE,
            contextItems: [searchItem],
          }),
        );
      },
      icon: ICONS.Transcoder,
    });
  }

  if (
    formatFiles &&
    formatFiles.length &&
    contextItem.name === formatNames.SUBTITLES &&
    permissions.write
  ) {
    itemsMain.push({
      label: 'Create Transcription',
      action: () => (dispatch: AppDispatch, getState: GetState) => {
        const appState = getState();
        const subtitle = appState.filesReducer.assetSubtitles.find(
          (item) => item.format_id === contextItem.id,
        );
        if (!subtitle) {
          dispatch(errorMessage({ text: 'Subtitles not found' }));
          return;
        }
        const latestActiveVersionId = selectLatestActiveVersionId(
          appState,
          appState.pages.asset.assetId,
        );
        const versionId =
          getCurrentRouteParams().versionId || latestActiveVersionId;
        if (!versionId) {
          dispatch(errorMessage({ text: 'Something went wrong' }));
          return;
        }
        if (contextItem.asset_id) {
          // Check if the asset already has a transcription in the selected language
          const transcriptionProperties =
            selectTranscriptionPropertiesByAssetId(
              getState(),
              contextItem.asset_id,
              versionId,
            );
          if (
            subtitle.language &&
            transcriptionProperties.some(
              ({ language }) => language === subtitle.language,
            )
          ) {
            dispatch(
              openModal({
                id: CONFIRM_TRANSCRIBE_SUBTITLE_MODAL,
                contextItems: [
                  {
                    id: contextItem.id,
                    assetId: contextItem.asset_id,
                    versionId,
                    subtitleId: subtitle.id,
                  },
                ],
              }),
            );
          } else {
            dispatch(
              transcribeSubtitle({
                assetId: contextItem.asset_id,
                versionId,
                subtitleId: subtitle.id,
                overwrite: false,
              }),
            );
          }
        }
      },
      icon: ICONS.Transcription,
    });
  }

  if (
    permissions.write &&
    contextItem.name !== formatNames.ORIGINAL &&
    contextItem.name !== formatNames.SUBTITLES &&
    contextItem.name !== formatNames.WILDMOKA
  ) {
    itemsMain.push({
      label: 'Rename…',
      action: () => (dispatch: AppDispatch) => {
        dispatch(openModal({ id: FORMATS_PANEL_NEW_FORMAT, contextItems }));
      },
      icon: ICONS.Edit,
    });
  }

  if (permissions.write && contextItem.name === formatNames.SUBTITLES) {
    itemsMain.push({
      label: 'Edit…',
      action: () => (dispatch: AppDispatch, getState: GetState) => {
        const subtitles = selectSubtitles(getState());
        const subtitlesFile = subtitles[contextItem.id];
        if (subtitlesFile) {
          dispatch(
            openModal({
              id: FORMATS_PANEL_EDIT_SUBTITLES,
              contextItems: [subtitlesFile],
            }),
          );
        } else {
          dispatch(errorMessage({ text: 'Subtitles not found' }));
        }
      },
      icon: ICONS.Edit,
    });
  }

  if (permissions.delete) {
    itemsDelete.push({
      label: 'Delete…',
      action: () => (dispatch: AppDispatch) => {
        dispatch(
          openModal({
            id: FORMATS_PANEL_DELETE_FILESETS,
            contextItems: [searchItem],
          }),
        );
      },
      icon: ICONS.TrashBin,
    });
  }

  return {
    items: separateMenuGroups([itemsOpen, itemsMain, itemsDelete]),
  };
};

const FormatsPanelContextMenu = connectContextMenuConsumer(
  contextMenuConnector(getDefinition)(ContextMenu),
  CONTEXT_MENU_PANELS_FILES_FORMATS,
);

export default FormatsPanelContextMenu;
